import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router'
import {
  Button,
  ButtonBlock,
  FieldBlock,
  MultiSelectField,
  PageHeader,
  ProgressBar,
  SelectField,
  TextArea,
  useToast,
} from '@politechdev/blocks-design-system'
import {
  CanvasserSelectField,
  CardError,
  UserSelectField,
  View,
  ViewContainer,
} from 'components'
import { SelectableUser } from 'components/UserSelectField/types'
import { useRequest } from 'hooks'
import RequestException from 'utils/RequestException'
import {
  fetchCanvasserReviewReasons,
  formatForEdit,
  formatForUpdate,
  getPerformanceReview,
  getReviewReasonOptions,
  hasMissingFields,
  updatePerformanceReview,
} from './utils'
import { EditablePerformanceReview } from './types'
import { PULL_OPTIONS } from './constants'

const EditPerformanceReview = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { setToast } = useToast()
  const history = useHistory()
  const { id: idString } = useParams<{ id: string }>()

  const [performanceReview, setPerformanceReview] =
    useState<EditablePerformanceReview | null>(null)

  const updateField = (updates: Partial<EditablePerformanceReview>) => {
    setPerformanceReview(current => ({ ...current!, ...updates }))
  }

  const getPerformanceReviewRequest = useRequest(getPerformanceReview, {
    onSuccess: response => {
      setPerformanceReview(formatForEdit(response.performance_review))
    },
    onError: e => {
      if (e instanceof RequestException && e.status === 404) {
        history.push(`/not_found?from=${location.pathname}`)
      }
    },
  })

  const navigateToInspect = (id: number) => {
    history.push(`/performance_reviews/${id}`)
  }

  const updatePerformanceReviewRequest = useRequest(updatePerformanceReview, {
    onSuccess: response => {
      navigateToInspect(response.performance_review.id)
    },
    onError: () => {
      setToast({
        variant: 'error',
        message: t('Failed to update review'),
      })
    },
  })

  useEffect(() => {
    void getPerformanceReviewRequest.makeRequest(+idString)
  }, [idString])

  const handleSave = async (fields: EditablePerformanceReview) =>
    updatePerformanceReviewRequest.makeRequest(
      +idString,
      formatForUpdate(fields)
    )

  const fetchReviewReasonsRequest = useRequest(fetchCanvasserReviewReasons, {
    onError: () => {
      setToast({
        variant: 'error',
        message: t('An issue occured fetching reasons for review'),
      })
    },
  })

  useEffect(() => {
    const canvasserId = performanceReview?.canvasser.id
    if (canvasserId) {
      void fetchReviewReasonsRequest.makeRequest(canvasserId)
    }
  }, [performanceReview?.canvasser.id])

  const reasonOptions = fetchReviewReasonsRequest.response
    ? getReviewReasonOptions(fetchReviewReasonsRequest.response)
    : []

  if (getPerformanceReviewRequest.hasErrors) {
    return (
      <CardError
        hide={false}
        message={t(
          'An error ocurred while trying to fetch the information for this performance review.'
        )}
      />
    )
  }

  if (!getPerformanceReviewRequest.isRequestComplete || !performanceReview)
    return <ProgressBar show />

  return (
    <View>
      <PageHeader title={t('New performance reviews')} />
      <ViewContainer>
        <FieldBlock collapsed>
          <UserSelectField
            label={t('Requested by')}
            user={performanceReview.requested_by_user}
            onSelect={(user: SelectableUser | undefined) =>
              updateField({ requested_by_user: user })
            }
            required
          />
        </FieldBlock>
        <FieldBlock collapsed>
          <CanvasserSelectField
            label={t('Canvasser under review')}
            selectedCanvasser={performanceReview.canvasser}
            onSelect={canvasser => canvasser && updateField({ canvasser })}
            filters={undefined}
            required
            disableNonCollectingTurfs={false}
          />
        </FieldBlock>
        <FieldBlock collapsed>
          <UserSelectField
            label={t('Assigned for review')}
            user={performanceReview.assigned_to_user}
            onSelect={(user: SelectableUser | undefined) =>
              updateField({ assigned_to_user: user })
            }
          />
        </FieldBlock>
        <FieldBlock collapsed>
          <SelectField
            label={t('Pull canvasser from field')}
            options={PULL_OPTIONS}
            onSelect={value => {
              updateField({
                pull_from_field: value,
              })
            }}
            value={performanceReview.pull_from_field}
            required
          />
        </FieldBlock>
        <FieldBlock collapsed variant="large">
          <MultiSelectField
            disabled={!performanceReview.canvasser}
            label={t('Reason for review')}
            loading={fetchReviewReasonsRequest.isLoading}
            values={performanceReview.reasons}
            options={reasonOptions}
            onSelect={values =>
              updateField({
                reasons: values,
              })
            }
            icon={undefined}
            required
          />
        </FieldBlock>
        <FieldBlock collapsed variant="large">
          <TextArea
            label={t('Additional details')}
            onChange={value =>
              updateField({
                additional_details: value as string,
              })
            }
            value={performanceReview.additional_details}
            required
          />
        </FieldBlock>
        <ButtonBlock>
          <Button.Accent
            onClick={() => handleSave(performanceReview)}
            disabled={
              updatePerformanceReviewRequest.isLoading ||
              hasMissingFields(performanceReview)
            }
          >
            {t('Save performance review')}
          </Button.Accent>
          <Button.Secondary
            onClick={() => navigateToInspect(+idString)}
            disabled={updatePerformanceReviewRequest.isLoading}
          >
            {t('Cancel')}
          </Button.Secondary>
        </ButtonBlock>
      </ViewContainer>
    </View>
  )
}

export default EditPerformanceReview
